import React, { useCallback, useMemo, useState } from 'react'
import { Carousel } from 'react-responsive-carousel'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import Image from 'next/image'

import { ExperimentCarouselProps } from './experiment-carousel.props'

const { useBreakpoints } = checkoutHooks

export const MOBILE_FEATURE_IMAGE_WIDTH = 296
const MOBILE_FEATURE_IMAGE_HEIGHT = 457
export const DESKTOP_FEATURE_IMAGE_WIDTH = 432
const DESKTOP_FEATURE_IMAGE_HEIGHT = 667

export const ExperimentCarousel = (props: ExperimentCarouselProps) => {
  const { onCardChange, onItemClick, images } = props
  const { isMobile } = useBreakpoints()
  const [lastCardIndex, setLastCardIndex] = useState(1)

  const onChange = useCallback(
    (index: number) => {
      const destinationIndex = index + 1
      if (destinationIndex !== lastCardIndex) {
        const swipeDirection = destinationIndex > lastCardIndex ? 'forward' : 'backward'
        onCardChange(lastCardIndex, swipeDirection)
        setLastCardIndex(destinationIndex)
      }
    },
    [lastCardIndex, onCardChange]
  )

  const onClickItem = useCallback(() => {
    onItemClick(lastCardIndex)
  }, [lastCardIndex, onItemClick])

  const imagesComponents = useMemo(() => {
    return images.map((image, index) => {
      const testId = 'experimentModal.experimentCarousel.image'
      const width = isMobile ? MOBILE_FEATURE_IMAGE_WIDTH : DESKTOP_FEATURE_IMAGE_WIDTH
      const height = isMobile ? MOBILE_FEATURE_IMAGE_HEIGHT : DESKTOP_FEATURE_IMAGE_HEIGHT
      const alt = `PFF Feature ${index + 1}`

      if (typeof image === 'string') {
        return (
          <Image
            data-testid={testId}
            key={image}
            src={image}
            width={width}
            height={height}
            alt={alt}
          />
        )
      }

      return (
        // These images are expected to be served from our Cloudfront cached S3 buckets.
        // Unfortunately the NextJS image tag doesn't support srcset so we have to use a regular img tag.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          data-testid={testId}
          key={image.lg}
          srcSet={isMobile ? image.sm : image.lg}
          width={width}
          height={height}
          alt={alt}
        />
      )
    })
    // disabling the next line since images won't change often and it isn't worth stringifying them or checking length
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  return (
    <div data-testid='experimentModal.experimentCarousel'>
      <Carousel
        centerMode
        emulateTouch
        autoPlay={false}
        showStatus={false}
        showThumbs={false}
        showIndicators={false}
        onChange={onChange}
        centerSlidePercentage={100}
        renderArrowPrev={() => null}
        renderArrowNext={() => null}
        onClickItem={onClickItem}
      >
        {imagesComponents}
      </Carousel>
    </div>
  )
}
